/**
 * HeroIllustration.tsx — isometrisk illustrasjon til hero (server, sky, sikkerhet)
 *
 * Tegnet som inline SVG i samme stil som ibm.com sine produktillustrasjoner:
 * isometriske plattformer med tårn, sky og skjold, forbundet med prikkede
 * linjer. Plattformer og tårn følger temaet (se illustrations.module.css);
 * aksentene er Carbon Blue/Purple.
 */

import { useId } from 'react';
import { Box, iso } from '@/components/product/iso';
import styles from '@/components/product/illustrations.module.css';

interface HeroIllustrationProps {
  label: string;
}

/** Rader og LED-er på en tårnflate (venstre flate: vender mot +y) */
function TowerFace({ x, y, z, w, d, h, rows }: { x: number; y: number; z: number; w: number; d: number; h: number; rows: number }) {
  const out = [];
  for (let i = 0; i < rows; i++) {
    const v0 = 0.08 + (i * 0.84) / rows;
    const v1 = v0 + 0.84 / rows - 0.05;
    const p = (u: number, v: number) => iso(x + u * w, y + d, z + v * h);
    out.push(
      <polygon
        key={i}
        className={styles.towerRow}
        points={`${p(0.14, v0)} ${p(0.86, v0)} ${p(0.86, v1)} ${p(0.14, v1)}`}
      />,
    );
    out.push(
      <polygon
        key={`led-${i}`}
        className={styles.towerLed}
        points={`${p(0.7, v0 + 0.01)} ${p(0.82, v0 + 0.01)} ${p(0.82, v1 - 0.01)} ${p(0.7, v1 - 0.01)}`}
      />,
    );
  }
  return <>{out}</>;
}

export default function HeroIllustration({ label }: HeroIllustrationProps) {
  const uid = useId();
  const blue = `${uid}-blue`;
  const purple = `${uid}-purple`;

  // Plattformer (x0, y0) — hver er 5 x 5 enheter og 0.3 tykk
  const A = { x: -2.5, y: 0.5 }; // tårn
  const B = { x: -2.5, y: -5.5 }; // skjold
  const C = { x: 3.5, y: -3.5 }; // sky
  const size = 5;

  const shieldAt = iso(B.x + size / 2, B.y + size / 2, 0.3);
  const [sx, sy] = shieldAt.split(',').map(Number);
  const cloudAt = iso(C.x + size / 2, C.y + size / 2, 0.3);
  const [cx, cy] = cloudAt.split(',').map(Number);

  return (
    <svg className={styles.scene} viewBox="110 60 660 400" role="img" aria-label={label}>
      <defs>
        <linearGradient id={blue} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#78a9ff" />
          <stop offset="1" stopColor="#0f62fe" />
        </linearGradient>
        <linearGradient id={purple} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#4589ff" />
          <stop offset="1" stopColor="#8a3ffc" />
        </linearGradient>
      </defs>

      {/* Prikkede forbindelser mellom plattformene */}
      <polyline className={styles.link} points={`${iso(A.x + size, A.y + 2, 0)} ${iso(C.x, C.y + 4.5, 0)}`} />
      <polyline className={styles.link} points={`${iso(B.x + size, B.y + 3, 0)} ${iso(C.x + 0.5, C.y, 0)}`} />
      <polyline className={styles.link} points={`${iso(A.x + 2, A.y, 0)} ${iso(B.x + 2.5, B.y + size, 0)}`} />

      {/* Bakerste plattform: skjold */}
      <Box x={B.x} y={B.y} z={0} w={size} d={size} h={0.3} top={styles.top} left={styles.left} right={styles.right} />
      <path
        transform={`translate(${sx - 36} ${sy - 112}) scale(1.5)`}
        d="M24 0 L48 8 V40 C48 62 36 74 24 80 C12 74 0 62 0 40 V8 Z"
        fill={`url(#${blue})`}
      />
      <path
        transform={`translate(${sx - 36} ${sy - 112}) scale(1.5)`}
        d="M24 8 L40 14 V40 C40 56 32 66 24 70 Z"
        fill="#ffffff"
        opacity="0.28"
      />

      {/* Høyre plattform: sky */}
      <Box x={C.x} y={C.y} z={0} w={size} d={size} h={0.3} top={styles.top} left={styles.left} right={styles.right} />
      <g transform={`translate(${cx} ${cy - 44})`}>
        <circle cx="-20" cy="6" r="22" fill={`url(#${purple})`} />
        <circle cx="6" cy="-8" r="28" fill={`url(#${purple})`} />
        <circle cx="30" cy="8" r="20" fill={`url(#${purple})`} />
        <rect x="-40" y="6" width="90" height="22" rx="11" fill={`url(#${purple})`} />
      </g>

      {/* Fremre plattform: server-tårn */}
      <Box x={A.x} y={A.y} z={0} w={size} d={size} h={0.3} top={styles.top} left={styles.left} right={styles.right} />
      <Box
        x={A.x + 1.2}
        y={A.y + 1}
        z={0.3}
        w={1.6}
        d={1.6}
        h={4.4}
        top={styles.towerTop}
        left={styles.towerLeft}
        right={styles.towerRight}
      />
      <TowerFace x={A.x + 1.2} y={A.y + 1} z={0.3} w={1.6} d={1.6} h={4.4} rows={9} />
      <Box
        x={A.x + 3.1}
        y={A.y + 2.2}
        z={0.3}
        w={1.3}
        d={1.3}
        h={2.8}
        top={styles.towerTop}
        left={styles.towerLeft}
        right={styles.towerRight}
      />
      <TowerFace x={A.x + 3.1} y={A.y + 2.2} z={0.3} w={1.3} d={1.3} h={2.8} rows={6} />
    </svg>
  );
}
